import { styled, Grid, Stack, Typography, Chip, Card, IconButton, Tooltip } from "@mui/material";
import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import useCountMaxTags from "../../../Utils/Hooks/useCountMaxTags";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useTranslation } from "react-i18next";

export interface ContractDetailsCardProps {
  contractId: number;
  contractTitle: string;
  customerName: string;
  broadcaster: string;
  mediaName: string;
  id: string;
  workId: string;
  externalId: string;
  startDate: string;
  endDate: string;
  rightType: string;
  languages: string[];
  territories: string[];
  titleType?: string;
  okDelivery?: boolean;
  small?: boolean;
  showAllLangsAndTerritories?: boolean;
  territoriesString?: string;
  handleAddToBasket?: (id: string, territoriesString: string) => void;
}

const ContractDetailsCard: React.FC<ContractDetailsCardProps> = (props) => {
  const { t } = useTranslation();
  const { visibleTags: languageVisibleTags, hiddenTagsCount: hiddenLanguageTagsCount } = useCountMaxTags({
    tagList: props.languages,
    nbMax: props.showAllLangsAndTerritories ? 300 : 5,
  });
  const { visibleTags: territoriesVisibleTags, hiddenTagsCount: hiddenTerritoriesTagCount } = useCountMaxTags({
    tagList: props.territories,
    nbMax: props.showAllLangsAndTerritories ? 300 : props.small ? 4 : 5,
  });

  const { handleAddToBasket } = props;

  const gridItem = props.small ? 4 : 2;

  return (
    <StyledCard variant="outlined">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={600}>
            #{props.contractId} - {props.contractTitle}
          </Typography>
          {props.titleType && props.titleType === "Movie" && (
            <Chip size="small" icon={<MovieOutlinedIcon />} color="info" label={t("common:movie")} />
          )}
          {props.titleType && props.titleType === "Season" && (
            <Chip size="small" icon={<MovieOutlinedIcon />} color="warning" label={t("common:season")} />
          )}
        </Stack>
        {props.okDelivery !== undefined && props.okDelivery === false && (
          <Chip size="small" icon={<ClearIcon />} color="secondary" label={t("chip:label.notDelivered")} />
        )}
        {props.okDelivery !== undefined && props.okDelivery && (
          <Chip size="small" icon={<CheckIcon />} color="success" label={t("common:delivered")} />
        )}
      </Stack>

      <Grid container rowSpacing={2} columnSpacing={3} sx={{ mb: 2 }}>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("field:label.customerName")}
          </Typography>
          <Typography>{props.customerName}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:broadcaster")}
          </Typography>
          <Typography>{props.broadcaster ?? "-"}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:mediaNames")}
          </Typography>
          <Typography>{props.mediaName}</Typography>
        </Grid>
      </Grid>
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid item md={gridItem}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            ID
          </Typography>
          <Typography>{props.id}</Typography>
        </Grid>
        <Grid item md={gridItem}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:workId")}
          </Typography>
          <Typography>{props.workId}</Typography>
        </Grid>
        <Grid item md={gridItem}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("field:label.externalID")}
          </Typography>
          <Typography>{props.externalId}</Typography>
        </Grid>
        <Grid item md={gridItem}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:startDate")}
          </Typography>
          <Typography>{props.startDate}</Typography>
        </Grid>
        <Grid item md={gridItem}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:endDate")}
          </Typography>
          <Typography>{props.endDate}</Typography>
        </Grid>
        <Grid item md={gridItem}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:rightType")}
          </Typography>
          <Typography>{props.rightType}</Typography>
        </Grid>
        <Grid item md={12}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:languages")}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} spacing={0.5} mt={0.5}>
            {languageVisibleTags.map((lang, i) => (
              <Chip size="small" label={lang} key={i} />
            ))}{" "}
            {hiddenLanguageTagsCount > 0 && <Chip size="small" label={`+${hiddenLanguageTagsCount}`} />}
          </Stack>
        </Grid>
        <Grid item md={8}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:territories")}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} spacing={0.5} mt={0.5}>
            {territoriesVisibleTags.map((lang, i) => (
              <Chip size="small" label={lang} key={i} />
            ))}{" "}
            {hiddenTerritoriesTagCount > 0 && <Chip size="small" label={`+${hiddenTerritoriesTagCount}`} />}
          </Stack>
        </Grid>
        {handleAddToBasket && (
          <Grid md={4} item display="flex" justifyContent="flex-end" alignItems="flex-end">
            <Tooltip title={t("common:addToBasket")}>
              <IconButton onClick={() => handleAddToBasket(props.id, props.territoriesString || "")}>
                <ShoppingBagOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </StyledCard>
  );
};

export default ContractDetailsCard;

const StyledCard = styled(Card)(({ theme }) => ({
  padding: `${theme.spacing(2.5)} ${theme.spacing(2)}`,
}));
